import { useState } from "react";
import {
  Chip,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  MdCheck,
  MdEdit,
  MdOutlineNotes,
  MdWarningAmber,
} from "react-icons/md";
import { typographyStyles } from "constants/styles";
import { formatIncomeEntryMeters, OBSERVATION_REASON_MAP } from "helpers/incomeEntry";
import {
  IIncomeEntryArticleBundle,
  IIncomeEntryArticleBundleObservation,
} from "types";
import IncomeEntryBundleEditDialog from "./IncomeEntryBundleEditDialog";
import { formatDateOnlyDMY } from "helpers";
import FileDownloadButton from "components/Files/FileDownloadButton";
import { getIncomeEntryObservationPhotoDownloadUrl } from "constants/urls";

const reasonLabel = (reason: IIncomeEntryArticleBundleObservation["reason"]) =>
  OBSERVATION_REASON_MAP[reason] ?? reason;

interface IncomeEntryArticleObservationsListProps {
  incomeEntryId: number;
  mode: "edit" | "view";
  bundles: IIncomeEntryArticleBundle[];
  articleUnitsPerItem?: string | null;
  onBundleChange?: (bundle: IIncomeEntryArticleBundle) => void;
}

export default function IncomeEntryArticleObservationsList({
  incomeEntryId,
  mode,
  bundles,
  articleUnitsPerItem,
  onBundleChange,
}: IncomeEntryArticleObservationsListProps) {
  const [editingBundle, setEditingBundle] =
    useState<IIncomeEntryArticleBundle | null>(null);

  const handleSaveEdit = (updatedBundle: IIncomeEntryArticleBundle) => {
    if (mode === "view") return;
    onBundleChange?.(updatedBundle);
    setEditingBundle(null);
  };

  return (
    <>
      <List disablePadding dense>
        {bundles.map((bundle) => {
          const observationDetails = bundle.observations.filter((obs) => obs.note || (obs.id && obs.photo_file_id));
          const hasObservations = bundle.observations.length > 0;
          return (
            <ListItem
              key={bundle.id}
              disablePadding
              sx={{ display: "block", py: 0.75 }}
              divider={true}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {hasObservations ? (
                    <MdWarningAmber size={18} color="#ffa726" />
                  ) : (
                    <MdCheck size={18} color="#66bb6a" />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      ...typographyStyles.monospace,
                      color: hasObservations ? "warning.main" : "text.primary",
                    }}
                  >
                    F-{bundle.lote_externo}
                  </Typography>
                  {bundle.observations.map((obs, index) => (
                    <Chip
                      key={obs.id ?? index}
                      label={reasonLabel(obs.reason)}
                      color="warning"
                      size="small"
                    />
                  ))}
                  <Typography
                    variant="body2"
                    sx={{ ...typographyStyles.monospace }}
                  >
                    oc: {bundle.oc} · lote: {bundle.lote}{bundle.fecha_elaboracion ? ` · elaborado: ${formatDateOnlyDMY(bundle.fecha_elaboracion)}` : null}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    sx={typographyStyles.monospace}
                    color="text.secondary"
                  >
                    {bundle.amount_available} disponibles de {bundle.amount} totales
                    {articleUnitsPerItem && (
                      <> · {formatIncomeEntryMeters(bundle.amount_available, articleUnitsPerItem)} disponibles de {formatIncomeEntryMeters(bundle.amount, articleUnitsPerItem)} totales</>
                    )}
                  </Typography>
                  {mode === "edit" && (
                    <IconButton
                      size="small"
                      onClick={() => setEditingBundle(bundle)}
                      aria-label="Editar fardo"
                    >
                      <MdEdit size={18} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {observationDetails.length > 0 && (
                <Stack spacing={0.75} sx={{ mt: 0.75, pl: 3.25 }}>
                  {observationDetails.map((obs, index) => (
                    <Stack
                      key={obs.id ?? index}
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {obs.note && (
                        <>
                          <MdOutlineNotes size={15} style={{ flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">
                            {obs.note}
                          </Typography>
                        </>
                      )}
                      {obs.id !== undefined && Boolean(obs.photo_file_id) && (
                        <FileDownloadButton
                          requestUrl={getIncomeEntryObservationPhotoDownloadUrl(incomeEntryId, obs.id)}
                          label="Descargar foto"
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </ListItem>
          );
        })}
      </List>

      <IncomeEntryBundleEditDialog
        bundle={editingBundle}
        onClose={() => setEditingBundle(null)}
        onSave={handleSaveEdit}
      />
    </>
  );
}
